import React from 'react';

const DayWeather = ({ dayNumber, weatherData, loading, error }) => {
  // Weather is supplementary — never block display
  if (error || (!loading && !weatherData)) {
    return null;
  }

  if (loading) {
    return (
      <div className="not-prose bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-lg px-4 py-3 my-3 animate-pulse">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-sky-200 rounded-full"></div>
          <div className="h-4 bg-sky-200 rounded w-48"></div>
        </div>
      </div>
    );
  }

  // Skip if the day has a skip reason (e.g., > 16 days out)
  if (weatherData.skip_reason) {
    return null;
  }

  const {
    description,
    emoji,
    temp_max_f,
    temp_min_f,
    temp_max_c,
    temp_min_c,
    precipitation_mm,
    wind_speed_kmh,
    location_name,
    date: dateStr,
    is_seasonal_estimate,
  } = weatherData;

  // Format the date for display
  let formattedDate = '';
  if (dateStr) {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      formattedDate = d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      formattedDate = dateStr;
    }
  }

  return (
    <div className="not-prose bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-lg px-4 py-3 my-3">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="text-lg" role="img" aria-label={description}>
          {emoji}
        </span>
        <span className="font-semibold text-sky-900">{description}</span>
        {is_seasonal_estimate && (
          <span
            className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5"
            title="Typical weather for this time of year, based on historical averages. A live forecast will appear within ~16 days of your trip."
          >
            Typical
          </span>
        )}
        {location_name && (
          <>
            <span className="text-sky-400">·</span>
            <span className="text-sky-700">{location_name}</span>
          </>
        )}
        {formattedDate && (
          <>
            <span className="text-sky-400">·</span>
            <span className="text-sky-600">{formattedDate}</span>
          </>
        )}
      </div>

      {/* Details row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-sky-800">
        {temp_max_f != null && temp_min_f != null && (
          <span>
            <span role="img" aria-label="temperature">🌡️</span>{' '}
            High {Math.round(temp_max_f)}°F/{temp_max_c}°C{' '}
            Low {Math.round(temp_min_f)}°F/{temp_min_c}°C
          </span>
        )}
        {precipitation_mm != null && precipitation_mm > 0 && (
          <span>
            <span role="img" aria-label="precipitation">💧</span>{' '}
            {precipitation_mm} mm
          </span>
        )}
        {wind_speed_kmh != null && (
          <span>
            <span role="img" aria-label="wind">💨</span>{' '}
            {wind_speed_kmh} km/h
          </span>
        )}
      </div>

      {is_seasonal_estimate && (
        <p className="mt-1.5 text-xs text-sky-600/80 italic">
          Typical for this time of year — a live forecast appears within ~16 days of your trip.
        </p>
      )}
    </div>
  );
};

export default DayWeather;
